import type {
  AriClientProfile,
  AriCounterpartyProfile,
  AriInboxDealTagFilter,
  AriInboxFolder,
  AriInboxThread,
} from "@/types/inbox.types";
import { DEMO_CONTRACT_IDS, musicIndustryContracts } from "@/mockData/demoContractData";

export const ARI_DEAL_STAGES = [
  "Prospecting",
  "Under Review",
  "Negotiation",
  "Pending Signature",
  "Signed",
  "Closed",
] as const;

/** Inbox thread opened as the Ari demo deal room after guided deal creation. */
export const ARI_DEMO_DEAL_ROOM_THREAD_ID = "ari-thread-1";

export const ARI_INBOX_FOLDERS: Array<{
  id: AriInboxFolder;
  label: string;
  badge?: number;
}> = [
  { id: "inbox", label: "Inbox", badge: 3 },
  { id: "messages", label: "Messages", badge: 1 },
  { id: "drafts", label: "Drafts" },
  { id: "sent", label: "Sent" },
  { id: "important", label: "Important" },
];

/** Deal-type tags shown in inbox sidebar filters (matches pipeline deal labels). */
export const ARI_INBOX_DEAL_TAG_FILTERS: AriInboxDealTagFilter[] = [
  "music-deal",
  "brand-deal",
  "endorsement",
  "licensing",
  "touring",
  "sports-deal",
  "publishing",
];

export const ARI_LABELS: Record<string, { name: string }> = {
  "music-deal": {
    name: "Music Deal",
  },
  publishing: {
    name: "Publishing Deal",
  },
  "brand-deal": {
    name: "Brand Deal",
  },
  endorsement: {
    name: "Endorsement",
  },
  licensing: {
    name: "Licensing Agreement",
  },
  touring: {
    name: "Touring Rights",
  },
  "sports-deal": {
    name: "Sports Deal",
  },
  "jay-z": {
    name: "Jay-Z",
  },
  rihanna: {
    name: "Rihanna",
  },
  lebron: {
    name: "LeBron James",
  },
  kobe: {
    name: "Kobe Bryant",
  },
  beyonce: {
    name: "Beyonce",
  },
  travis: {
    name: "Travis Scott",
  },
  serena: {
    name: "Serena Williams",
  },
};

export const ARI_INBOX_THREADS: AriInboxThread[] = [
  {
    id: "ari-thread-1",
    unread: true,
    starred: true,
    from: {
      name: "Marcus Webb",
      email: "marcus.webb@roc-nation.com",
      initials: "MW",
    },
    subject: "Jay-Z Publishing Rights - Catalog Acquisition Proposal",
    date: "Apr 27, 2026",
    time: "9:41 AM",
    dealTags: ["music-deal", "publishing"],
    clientTags: ["jay-z"],
    keywords: ["publishing rights", "catalog", "streaming royalties", "Tidal"],
    pipeline: { stage: "Negotiation", progress: 3 },
    messages: [
      {
        id: "ari-1a",
        sender: "Marcus Webb",
        email: "marcus.webb@roc-nation.com",
        date: "Apr 27, 9:41 AM",
        preview:
          "Shawn has greenlit the catalog acquisition discussion. Master license: $85M upfront plus 22% streaming revenue.",
        body:
          "Team,\n\nFollowing the sit-down last week, Shawn has greenlit the catalog acquisition discussion. We are looking at a structure that keeps streaming royalties with Roc Nation while licensing the master rights for a 15-year term.\n\nKey terms on the table:\n- Master license: $85M upfront plus 22% of net streaming revenue\n- Publishing catalog included (450+ compositions)\n- Tidal integration clause with exclusive windowing rights for 30 days on new releases\n- Reversion clause at year 10 with right of first refusal\n\nWe need your legal team's redline back by end of next week. Shawn wants to close before Q3.\n\nMarcus\nSVP Business Affairs, Roc Nation",
      },
      {
        id: "ari-1b",
        sender: "You",
        email: "mayowa@equal-iq.com",
        date: "Apr 27, 10:30 AM",
        preview:
          "Strong structure. We will have IP review the reversion clause and clarify Tidal windowing.",
        isCurrentUser: true,
        body:
          "Marcus,\n\nThis is a strong structure. We will have our IP team review the reversion clause. The year 10 right of first refusal is the key sticking point.\n\nAre we aligned on the Tidal windowing being limited to audio-only, or does this extend to visual albums and documentaries?",
      },
      {
        id: "ari-1c",
        sender: "Marcus Webb",
        email: "marcus.webb@roc-nation.com",
        date: "Apr 27, 11:15 AM",
        preview: "Tidal windowing is audio-only. Visual projects are handled separately.",
        body:
          "Good catch. Tidal windowing is audio-only for now. Visual projects are handled separately under the film and TV division. We can add that clarification in the redline.",
      },
    ],
    ariAlerts: [
      {
        type: "action",
        text: "Redline term sheet is due by Thursday EOD - not yet submitted.",
        urgency: "high",
      },
      {
        type: "info",
        text: "Roc Nation has been waiting 2 days for your legal redline response.",
        urgency: "medium",
      },
      {
        type: "insight",
        text: "This is Jay-Z's 3rd outreach this quarter - high engagement signal.",
        urgency: "low",
      },
    ],
    attachments: [
      {
        name: "Catalog_Acquisition_TermSheet_v3.pdf",
        type: "pdf",
        size: "2.4 MB",
        date: "Apr 27",
        status: "Needs Signature",
        tone: "warning",
      },
      {
        name: "Roc_Nation_DD_Checklist.pdf",
        type: "pdf",
        size: "890 KB",
        date: "Apr 27",
        status: "Received",
        tone: "success",
      },
    ],
    aiSummary:
      "Roc Nation proposes a 15-year master license for Jay-Z's catalog: $85M upfront plus 22% streaming revenue, with Tidal windowing and a year-10 reversion clause.",
  },
  {
    id: "ari-thread-2",
    unread: true,
    starred: false,
    from: {
      name: "Dominique Azari",
      email: "d.azari@fentygroup.com",
      initials: "DA",
    },
    subject: "Savage x Fenty x Nike Collab - Final Term Sheet",
    date: "Apr 27, 2026",
    time: "8:22 AM",
    dealTags: ["brand-deal", "endorsement"],
    clientTags: ["rihanna"],
    keywords: ["Savage x Fenty", "Nike collab", "apparel line", "Spring 2027"],
    pipeline: { stage: "Pending Signature", progress: 4 },
    messages: [
      {
        id: "ari-2a",
        sender: "Dominique Azari",
        email: "d.azari@fentygroup.com",
        date: "Apr 27, 8:22 AM",
        preview:
          "Nike final term sheet: 60/40 revenue split, 2M guaranteed units, Spring 2027.",
        body:
          "Hi,\n\nNike Brand Partnerships came back with the final term sheet for the Savage x Fenty collection.\n\nUpdates from the last draft:\n- Revenue split revised to 60/40 in Fenty's favor\n- Minimum guaranteed units: 2M pieces for Spring 2027 launch\n- Rihanna retains full creative direction and approval rights\n- Co-branded retail rollout across Nike flagship stores and SavagexFenty.com\n- 18-month exclusivity window\n\nRihanna has reviewed and is happy with the creative terms. The exclusivity clause is the last open item. She wants it down to 12 months.\n\nCan you push back on that point before we countersign?\n\nDom",
      },
    ],
    ariAlerts: [
      {
        type: "action",
        text: "Final term sheet is ready for countersignature - exclusivity clause still open.",
        urgency: "high",
      },
      {
        type: "action",
        text: "Nike countersign deadline is in 3 days. Push back on exclusivity before signing.",
        urgency: "high",
      },
    ],
    attachments: [
      {
        name: "SXF_x_Nike_TermSheet_Final.pdf",
        type: "pdf",
        size: "1.8 MB",
        date: "Apr 27",
        status: "Needs Countersign",
        tone: "danger",
      },
      {
        name: "Exclusivity_Clause_Amendment.docx",
        type: "docx",
        size: "340 KB",
        date: "Apr 27",
        status: "In Review",
        tone: "warning",
      },
    ],
    aiSummary:
      "Nike final term sheet for Savage x Fenty moves revenue to 60/40 and locks a Spring 2027 launch. Rihanna approves creative terms but wants exclusivity reduced to 12 months.",
  },
  {
    id: "ari-thread-3",
    unread: true,
    starred: true,
    from: {
      name: "Klutch Sports Legal",
      email: "legal@klutchsports.com",
      initials: "KS",
    },
    subject: "LeBron x Nike Lifetime Deal - Royalty Amendment",
    date: "Apr 26, 2026",
    time: "Yesterday",
    dealTags: ["brand-deal", "endorsement"],
    clientTags: ["lebron"],
    keywords: ["Nike lifetime deal", "royalty structure", "equity stake", "LBJ"],
    pipeline: { stage: "Under Review", progress: 2 },
    messages: [
      {
        id: "ari-3a",
        sender: "Klutch Sports Legal",
        email: "legal@klutchsports.com",
        date: "Apr 26, 2:00 PM",
        preview:
          "Nike proposes royalty escalation to 5.5% after the LeBron line crossed $1B retail.",
        body:
          "Team,\n\nNike has proposed an amendment to the lifetime deal royalty structure triggered by the LeBron signature line crossing $1B in annual retail sales in FY2025.\n\nAmendment summary:\n- Royalty rate escalates from 4.5% to 5.5% effective FY2026\n- New performance bonus: $10M per year the line exceeds $1.2B\n- Equity stake conversion option for up to $50M of future royalties\n- Signature line continues post-retirement with LeBron maintaining creative input\n\nWe need your sign-off on the equity conversion clause before we respond. Rich Paul wants a call tomorrow morning.\n\nKlutch Legal",
      },
      {
        id: "ari-3b",
        sender: "You",
        email: "mayowa@equal-iq.com",
        date: "Apr 26, 3:45 PM",
        preview: "Negotiate the cap price based on 20-day VWAP instead of a fixed price.",
        isCurrentUser: true,
        body:
          "The equity conversion structure is smart. We should negotiate the cap price to be based on a 20-day VWAP rather than a fixed price to protect against short-term volatility. I am available tomorrow at 9am for the call with Rich.",
      },
    ],
    ariAlerts: [
      {
        type: "action",
        text: "Rich Paul call is tomorrow morning - equity conversion sign-off needed before 9am.",
        urgency: "high",
      },
      {
        type: "info",
        text: "LeBron line crossed $1B retail, triggering the royalty escalation clause.",
        urgency: "medium",
      },
    ],
    attachments: [
      {
        name: "Nike_Lifetime_Deal_Amendment_v2.pdf",
        type: "pdf",
        size: "3.1 MB",
        date: "Apr 26",
        status: "Under Review",
        tone: "info",
      },
      {
        name: "Equity_Conversion_Term_Summary.pdf",
        type: "pdf",
        size: "1.2 MB",
        date: "Apr 26",
        status: "Needs Sign-off",
        tone: "warning",
      },
    ],
    aiSummary:
      "Nike is amending LeBron's lifetime deal after signature sales crossed $1B. The clause increases royalties to 5.5%, adds a $10M bonus trigger, and introduces an equity conversion option.",
  },
  {
    id: "ari-thread-4",
    unread: false,
    starred: false,
    from: {
      name: "Vanessa Bryant",
      email: "v.bryant@mambasp.com",
      initials: "VB",
    },
    subject: "Kobe Nike Reacquisition - Licensing Structure Update",
    date: "Apr 25, 2026",
    time: "Apr 25",
    dealTags: ["licensing", "brand-deal"],
    clientTags: ["kobe"],
    keywords: ["Mamba brand", "Nike reacquisition", "Kobe line", "legacy licensing"],
    pipeline: { stage: "Negotiation", progress: 3 },
    messages: [
      {
        id: "ari-4a",
        sender: "Vanessa Bryant",
        email: "v.bryant@mambasp.com",
        date: "Apr 25, 10:00 AM",
        preview:
          "Mamba Sports Foundation is ready to discuss a new Nike licensing framework.",
        body:
          "Hi,\n\nFollowing the reacquisition of the Kobe rights from Nike, the Mamba and Mambacita Sports Foundation is ready to discuss the new licensing framework for the Kobe signature line.\n\nOur priorities:\n- Full creative control remains with the family\n- Licensing fees: 8% royalty on all Nike Kobe products\n- Foundation revenue share: 5% of net sales directed annually\n- Strict approval rights on co-branding and limited editions\n- No posthumous likeness use outside agreed categories without written consent\n\nWe want the right deal, not the fastest one.\n\nVanessa",
      },
    ],
    ariAlerts: [
      {
        type: "info",
        text: "First formal outreach from the Bryant estate - handle with care and sensitivity.",
        urgency: "medium",
      },
      {
        type: "insight",
        text: "No response sent yet. Vanessa Bryant sent this 3 days ago.",
        urgency: "medium",
      },
    ],
    attachments: [
      {
        name: "Mamba_Brand_Licensing_Framework.pdf",
        type: "pdf",
        size: "2.0 MB",
        date: "Apr 25",
        status: "Received",
        tone: "success",
      },
    ],
    aiSummary:
      "Vanessa Bryant proposes new Kobe Nike licensing with an 8% royalty, 5% foundation share, family creative control, and strict consent for likeness use.",
  },
  {
    id: "ari-thread-5",
    unread: false,
    starred: false,
    from: {
      name: "Parkwood Legal",
      email: "legal@parkwoodentertainment.com",
      initials: "PL",
    },
    subject: "Renaissance World Tour - Merch & Venue Rights Amendment",
    date: "Apr 25, 2026",
    time: "Apr 25",
    dealTags: ["touring", "music-deal"],
    clientTags: ["beyonce"],
    keywords: ["Renaissance Tour", "venue rights", "merch split", "Live Nation"],
    pipeline: { stage: "Signed", progress: 5 },
    messages: [
      {
        id: "ari-5a",
        sender: "Parkwood Legal",
        email: "legal@parkwoodentertainment.com",
        date: "Apr 25, 9:15 AM",
        preview:
          "Live Nation amends Renaissance II merch split to 80/20. Netflix streaming clause needs review.",
        body:
          "Team,\n\nLive Nation has proposed amendments to the venue rights and merchandise split for the Renaissance II leg.\n\nKey changes:\n- Venue merch split revised to 80/20 in Parkwood's favor\n- VIP package revenue fully retained by Parkwood\n- Streaming rights for select tour dates, with Netflix negotiated separately\n- Hotel and travel logistics handled by Live Nation\n\nBeyonce's team has approved the merch and VIP terms. The Netflix streaming clause needs your review to ensure master recording ownership stays with Parkwood post-broadcast.\n\nParkwood Legal",
      },
    ],
    ariAlerts: [
      {
        type: "action",
        text: "Netflix streaming clause needs review before countersigning.",
        urgency: "high",
      },
      {
        type: "info",
        text: "80/20 merch split and VIP terms are already approved.",
        urgency: "low",
      },
    ],
    attachments: [
      {
        name: "Renaissance_II_LiveNation_Amendment.pdf",
        type: "pdf",
        size: "4.2 MB",
        date: "Apr 25",
        status: "Partially Signed",
        tone: "warning",
      },
      {
        name: "Netflix_Streaming_Rights_Addendum.pdf",
        type: "pdf",
        size: "1.5 MB",
        date: "Apr 25",
        status: "Needs Review",
        tone: "danger",
      },
    ],
    aiSummary:
      "Live Nation improves the Renaissance II merch split and VIP terms. Only the Netflix streaming clause remains open, especially master recording ownership after broadcast.",
  },
  {
    id: "ari-thread-6",
    unread: false,
    starred: false,
    from: {
      name: "Cactus Jack Mgmt",
      email: "deals@cactusjack.com",
      initials: "CJ",
    },
    subject: "Travis Scott x McDonald's Phase 2 - Global Expansion",
    date: "Apr 24, 2026",
    time: "Apr 24",
    dealTags: ["brand-deal"],
    clientTags: ["travis"],
    keywords: ["McDonald's", "Cactus Jack", "meal deal", "global rollout"],
    pipeline: { stage: "Prospecting", progress: 1 },
    messages: [
      {
        id: "ari-6a",
        sender: "Cactus Jack Mgmt",
        email: "deals@cactusjack.com",
        date: "Apr 24, 3:30 PM",
        preview:
          "McDonald's Phase 2 global rollout: $20M flat fee, merch collab, 15 markets.",
        body:
          "Yo,\n\nMcD corporate is ready to pull the trigger on Phase 2 of the Cactus Jack collab - this time a global rollout hitting 15 markets.\n\nDeal structure:\n- Flat fee: $20M for Travis's likeness and creative involvement\n- Merch collab: Cactus Jack x McDonald's limited drop\n- Revenue from merch split: 70/30 in Travis's favor\n- Three new market-specific meal names curated by Travis\n- No exclusivity in fast food, only QSR exclusivity with McDonald's for 12 months\n\nTravis is in. We need your team to review the IP ownership clause on the merch.\n\nCactus Jack Mgmt",
      },
    ],
    ariAlerts: [
      {
        type: "action",
        text: "IP ownership clause on the merch deal needs legal review before Travis signs.",
        urgency: "high",
      },
      {
        type: "insight",
        text: "Travis has completed 3 brand deals through this channel - high conversion rate.",
        urgency: "low",
      },
    ],
    attachments: [
      {
        name: "McDonalds_CactusJack_Phase2_TermSheet.pdf",
        type: "pdf",
        size: "2.8 MB",
        date: "Apr 24",
        status: "Draft",
        tone: "neutral",
      },
      {
        name: "Merch_IP_Ownership_Clause.docx",
        type: "docx",
        size: "410 KB",
        date: "Apr 24",
        status: "Needs Legal Review",
        tone: "danger",
      },
    ],
    aiSummary:
      "McDonald's proposes a $20M global rollout for Travis Scott with merch revenue split 70/30. Travis approved, but merch IP ownership needs legal review.",
  },
  {
    id: "ari-thread-7",
    unread: false,
    starred: false,
    from: {
      name: "WTA Partnerships",
      email: "partnerships@wta.com",
      initials: "WT",
    },
    subject: "Serena Williams x Wilson - Legacy Endorsement Renewal",
    date: "Apr 24, 2026",
    time: "Apr 24",
    dealTags: ["sports-deal", "endorsement"],
    clientTags: ["serena"],
    keywords: ["Serena Ventures", "Wilson", "racket deal", "legacy endorsement"],
    pipeline: { stage: "Closed", progress: 6 },
    messages: [
      {
        id: "ari-7a",
        sender: "WTA Partnerships",
        email: "partnerships@wta.com",
        date: "Apr 24, 11:00 AM",
        preview:
          "Wilson proposes $20M, 5-year post-retirement legacy endorsement.",
        body:
          "Hi,\n\nWilson Sporting Goods has approached us about renewing Serena's legacy endorsement deal post-retirement. This would be a first-of-its-kind post-career endorsement structure for the sport.\n\nProposed terms:\n- Annual fee: $4M for 5 years\n- Serena signature racket line continues\n- 10% royalty on all Serena signature products\n- Wilson commits to $2M annual donation to the Serena Williams Foundation\n- No active play requirements\n- Serena retains SW monogram and signature design globally\n\nThis is a clean deal. Serena's team is reviewing and we expect a yes pending IP confirmation.\n\nWTA Partnerships",
      },
    ],
    ariAlerts: [
      {
        type: "info",
        text: "Deal is clean and favorable. Serena team expects yes pending IP confirmation.",
        urgency: "low",
      },
      {
        type: "info",
        text: "Wilson has been waiting 4 days for a response. Consider acknowledging today.",
        urgency: "medium",
      },
    ],
    attachments: [
      {
        name: "Wilson_Legacy_Endorsement_Proposal.pdf",
        type: "pdf",
        size: "1.6 MB",
        date: "Apr 24",
        status: "Received",
        tone: "success",
      },
    ],
    aiSummary:
      "Wilson proposes a $20M, five-year legacy endorsement for Serena with a signature racket line, 10% royalty, foundation donation, and limited event appearances.",
  },
];

export const ARI_DRAFT_THREADS: AriInboxThread[] = [
  {
    ...ARI_INBOX_THREADS[0],
    id: "ari-draft-1",
    unread: false,
    starred: false,
    subject: "Draft: Roc Nation catalog redline response",
    time: "Updated 14 min ago",
    date: "Today",
    messages: [
      {
        id: "ari-draft-1a",
        sender: "You",
        email: "mayowa@equal-iq.com",
        date: "Draft",
        preview: "Marcus, attached is our redline with proposed language for the reversion clause...",
        isCurrentUser: true,
        body:
          "Marcus,\n\nAttached is our redline with proposed language for the reversion clause and an audio-only limitation on Tidal windowing. We can turn comments by Thursday EOD.",
      },
    ],
    ariAlerts: [],
    attachments: [],
    aiSummary:
      "Draft response for Roc Nation catalog redline. Needs final legal attachment before sending.",
  },
  {
    ...ARI_INBOX_THREADS[1],
    id: "ari-draft-2",
    unread: false,
    starred: false,
    subject: "Draft: Nike exclusivity pushback",
    time: "Updated 1 hr ago",
    date: "Today",
    messages: [
      {
        id: "ari-draft-2a",
        sender: "You",
        email: "mayowa@equal-iq.com",
        date: "Draft",
        preview: "Dom, we can push Nike to 12 months with a category carve-out...",
        isCurrentUser: true,
        body:
          "Dom,\n\nWe can push Nike to 12 months with a category carve-out for non-performance intimate apparel. I am drafting the clause now.",
      },
    ],
    ariAlerts: [],
    attachments: [],
    aiSummary: "Draft response to reduce exclusivity from 18 months to 12 months.",
  },
];

export const ARI_SMART_REPLIES: Record<string, string[]> = {
  "ari-thread-1": [
    "I will send the redline Thursday.",
    "Audio-only windowing works for us.",
    "Looping legal in now.",
  ],
  "ari-thread-2": [
    "We will push for 12 months.",
    "Creative terms look approved.",
    "I will revise exclusivity language.",
  ],
  "ari-thread-3": [
    "VWAP cap language is preferred.",
    "I can join the 9am call.",
    "Sending clause comments shortly.",
  ],
  default: [
    "Thanks, I am reviewing this.",
    "I will follow up today.",
    "Can we schedule a quick call?",
  ],
};

export interface AriTextThread {
  id: string;
  title: string;
  subtitle: string;
  initials: string;
  unread: boolean;
  lastAt: string;
  preview: string;
  isAriThread?: boolean;
  messages: Array<{
    id: string;
    from: "ari" | "me" | "contact";
    sender?: string;
    body: string;
    time: string;
  }>;
}

export const ARI_TEXT_THREADS: AriTextThread[] = [
  {
    id: "im-ari",
    isAriThread: true,
    title: "Ari",
    subtitle: "AI assistant - iMessage",
    initials: "AI",
    unread: true,
    lastAt: "2:41 PM",
    preview: "Got it - I added the Nike renewal to your pipeline.",
    messages: [
      {
        id: "im-a1",
        from: "ari",
        body: "Morning - 3 deal threads need replies today. Want a prioritized list?",
        time: "9:12 AM",
      },
      {
        id: "im-a2",
        from: "me",
        body: "Yes, and flag anything over $2M.",
        time: "9:14 AM",
      },
      {
        id: "im-a3",
        from: "ari",
        body: "Done. Top: Nike renewal, Roc Nation catalog follow-up, and Wilson legacy doc.",
        time: "9:14 AM",
      },
      {
        id: "im-a4",
        from: "me",
        body: "Move Nike to negotiation stage.",
        time: "2:38 PM",
      },
      {
        id: "im-a5",
        from: "ari",
        body: "Got it - I added the Nike renewal to your pipeline.",
        time: "2:41 PM",
      },
    ],
  },
  {
    id: "im-lebron-nike",
    title: "LeBron + Nike",
    subtitle: "SMS group - Ari included",
    initials: "LN",
    unread: false,
    lastAt: "Yesterday",
    preview: "Ari: Drafted the royalty summary. Want me to paste it here?",
    messages: [
      {
        id: "im-l1",
        from: "contact",
        sender: "LeBron James",
        body: "Can we get the updated rev share table before Friday?",
        time: "Mon 4:12 PM",
      },
      {
        id: "im-l2",
        from: "me",
        body: "On it. Ari is pulling the latest from the vault.",
        time: "Mon 4:20 PM",
      },
      {
        id: "im-l3",
        from: "ari",
        body: "Drafted the royalty summary. Want me to paste it here?",
        time: "Yesterday",
      },
    ],
  },
  {
    id: "im-marcus",
    title: "Marcus Chen",
    subtitle: "Roc Nation - mobile",
    initials: "MC",
    unread: false,
    lastAt: "Apr 28",
    preview: "Perfect - sending the redline tonight.",
    messages: [
      {
        id: "im-m1",
        from: "contact",
        sender: "Marcus Chen",
        body: "Shawn wants the master license term sheet by Wednesday.",
        time: "Apr 28, 11:02 AM",
      },
      {
        id: "im-m2",
        from: "me",
        body: "Perfect - sending the redline tonight.",
        time: "Apr 28, 11:18 AM",
      },
      {
        id: "im-m3",
        from: "ari",
        body: "I matched this thread to the Roc Nation deal room. Say the word and I will attach the latest PDF.",
        time: "Apr 28, 11:19 AM",
      },
    ],
  },
];

export const ARI_CLIENT_PROFILES: Record<string, AriClientProfile> = {
  "jay-z": {
    id: "jay-z",
    name: "Jay-Z (Shawn Carter)",
    title: "Artist, Entrepreneur and CEO, Roc Nation",
    initials: "JZ",
    outreachCount: 3,
    outreachNote: "3rd outreach this quarter",
    relationshipScore: 92,
    tags: ["music-deal", "publishing"],
    bio:
      "Founder of Roc Nation, TIDAL, and multiple equity ventures. One of the most commercially impactful artists in history with cross-industry holdings in music, sports, spirits, and real estate.",
    social: {
      wikipedia: "https://en.wikipedia.org/wiki/Jay-Z",
      instagram: "https://instagram.com/jayz",
      spotify: "https://open.spotify.com/artist/3nFkdlSjzX9mRTtwJOzDYB",
    },
    sources: [
      {
        title: "Forbes: Jay-Z becomes hip-hop's first billionaire",
        url: "https://www.forbes.com/sites/zackomalleygreenburg/2019/06/03/jay-z-hip-hop-first-billionaire/",
      },
      {
        title: "Billboard: Jay-Z's Roc Nation empire",
        url: "https://www.billboard.com/music/music-news/jay-z-roc-nation-15-years/",
      },
    ],
    pastDeals: [
      { name: "Tidal Equity Deal", year: 2015, status: "Closed", value: "$56M" },
      {
        name: "Armand de Brignac Partnership",
        year: 2021,
        status: "Signed",
        value: "$300M",
      },
      {
        name: "Blueprint Catalog License",
        year: 2023,
        status: "Signed",
        value: "$12M",
      },
    ],
  },
  rihanna: {
    id: "rihanna",
    name: "Rihanna (Robyn Fenty)",
    title: "Artist, Founder and CEO, Fenty Corp",
    initials: "RF",
    outreachCount: 2,
    outreachNote: "2nd outreach this month",
    relationshipScore: 88,
    tags: ["brand-deal", "endorsement"],
    bio:
      "Founder of Fenty Beauty and Savage x Fenty. Rihanna's brand partnerships are among the highest-grossing in entertainment and fashion.",
    social: {
      wikipedia: "https://en.wikipedia.org/wiki/Rihanna",
      instagram: "https://instagram.com/badgalriri",
      spotify: "https://open.spotify.com/artist/5pKCCKE2ajJHZ9KAiaK11H",
    },
    sources: [
      {
        title: "Forbes: Rihanna billionaire status via Fenty Beauty",
        url: "https://www.forbes.com/sites/ryanmac/2021/08/04/rihanna-is-now-a-billionaire/",
      },
    ],
    pastDeals: [
      { name: "Fenty Beauty Launch Deal", year: 2017, status: "Closed", value: "$71M" },
      { name: "Savage x Fenty Vol. 1", year: 2022, status: "Signed", value: "$25M" },
    ],
  },
  lebron: {
    id: "lebron",
    name: "LeBron James",
    title: "NBA All-Star, Co-Founder SpringHill Co.",
    initials: "LJ",
    outreachCount: 5,
    outreachNote: "5th outreach - high priority relationship",
    relationshipScore: 97,
    tags: ["brand-deal", "sports-deal"],
    bio:
      "One of the most commercially powerful athletes ever, with ventures spanning media, consumer products, and long-term Nike brand economics.",
    social: {
      wikipedia: "https://en.wikipedia.org/wiki/LeBron_James",
      instagram: "https://instagram.com/kingjames",
    },
    sources: [
      {
        title: "Forbes: LeBron James joins billionaire club",
        url: "https://www.forbes.com/sites/kurtbadenhausen/2022/06/02/lebron-james-joins-the-billionaires-club/",
      },
    ],
    pastDeals: [
      { name: "Nike Lifetime Deal", year: 2015, status: "Signed", value: "$1B+" },
      { name: "SpringHill Media Deal", year: 2021, status: "Closed", value: "$725M" },
      { name: "Lobos 1707 Brand Deal", year: 2022, status: "Signed", value: "$40M" },
    ],
  },
  kobe: {
    id: "kobe",
    name: "Kobe Bryant Estate",
    title: "Mamba and Mambacita Sports Foundation",
    initials: "KB",
    outreachCount: 1,
    outreachNote: "1st formal outreach from the estate",
    relationshipScore: 78,
    tags: ["licensing", "brand-deal"],
    bio:
      "The Bryant estate oversees the Mamba brand and foundation. The estate maintains strict brand integrity and approval standards.",
    social: {
      wikipedia: "https://en.wikipedia.org/wiki/Kobe_Bryant",
    },
    sources: [
      {
        title: "ESPN: Kobe Bryant legacy and basketball impact",
        url: "https://www.espn.com/nba/story/_/id/29391804/kobe-bryant-legacy-basketball-forever",
      },
    ],
    pastDeals: [
      { name: "Nike Kobe Line", year: 2003, status: "Expired", value: "$40M/yr" },
      { name: "Kobe Inc. Acquisition", year: 2016, status: "Closed", value: "$6M" },
    ],
  },
  beyonce: {
    id: "beyonce",
    name: "Beyonce Knowles-Carter",
    title: "Artist and CEO, Parkwood Entertainment",
    initials: "BK",
    outreachCount: 2,
    outreachNote: "2nd outreach via Parkwood Legal",
    relationshipScore: 85,
    tags: ["touring", "music-deal"],
    bio:
      "Founder of Parkwood Entertainment. Known for meticulous brand control and high-value long-term touring, film, and brand structures.",
    social: {
      wikipedia: "https://en.wikipedia.org/wiki/Beyonce",
      instagram: "https://instagram.com/beyonce",
      spotify: "https://open.spotify.com/artist/6vWDO969PvNqNYHIOW5v0m",
    },
    sources: [
      {
        title: "Rolling Stone: Renaissance tour milestones",
        url: "https://www.rollingstone.com/music/music-news/beyonce-renaissance-tour-highest-grossing/",
      },
    ],
    pastDeals: [
      {
        name: "Renaissance Tour Global Rights",
        year: 2023,
        status: "Closed",
        value: "$580M gross",
      },
      { name: "Ivy Park x Adidas", year: 2019, status: "Expired", value: "$20M/yr" },
    ],
  },
  travis: {
    id: "travis",
    name: "Travis Scott",
    title: "Artist, Founder Cactus Jack Records",
    initials: "TS",
    outreachCount: 3,
    outreachNote: "3rd engagement - ongoing brand partner",
    relationshipScore: 82,
    tags: ["brand-deal"],
    bio:
      "One of the top brand-collab artists globally. Cactus Jack partnerships span music, apparel, food, gaming, and footwear.",
    social: {
      wikipedia: "https://en.wikipedia.org/wiki/Travis_Scott",
      instagram: "https://instagram.com/travisscott",
      spotify: "https://open.spotify.com/artist/0Y5tJX1MQlPlqiwlOH1tJY",
    },
    sources: [
      {
        title: "Billboard: McDonald's Travis Scott meal impact",
        url: "https://www.billboard.com/music/music-news/travis-scott-mcdonalds-meal-huge-success/",
      },
    ],
    pastDeals: [
      { name: "McDonald's Cactus Jack Meal", year: 2020, status: "Closed", value: "$20M" },
      { name: "Fortnite Astronomical Event", year: 2020, status: "Closed", value: "$20M" },
      { name: "Nike Air Force 1 Collab", year: 2022, status: "Signed", value: "$8M" },
    ],
  },
  serena: {
    id: "serena",
    name: "Serena Williams",
    title: "Tennis Legend, Founder Serena Ventures",
    initials: "SW",
    outreachCount: 2,
    outreachNote: "2nd outreach - legacy endorsement phase",
    relationshipScore: 90,
    tags: ["sports-deal", "endorsement"],
    bio:
      "Tennis legend and founder of Serena Ventures. Post-retirement focus includes legacy brand partnerships, foundation work, and investing.",
    social: {
      wikipedia: "https://en.wikipedia.org/wiki/Serena_Williams",
      instagram: "https://instagram.com/serenawilliams",
    },
    sources: [
      {
        title: "Fortune: Serena Ventures startup investments",
        url: "https://fortune.com/2022/02/10/serena-williams-serena-ventures-fund/",
      },
    ],
    pastDeals: [
      { name: "Wilson 25-year Racket Deal", year: 2003, status: "Expired", value: "$5M/yr" },
      { name: "Nike Apparel Partnership", year: 2004, status: "Closed", value: "$40M" },
      { name: "S by Serena Fashion Line", year: 2018, status: "Active", value: "$12M" },
    ],
  },
};

export const ARI_COUNTERPARTY_PROFILES: Record<string, AriCounterpartyProfile> = {
  "roc-nation.com": {
    domain: "roc-nation.com",
    company: "Roc Nation",
    type: "Entertainment Co.",
    hq: "New York, NY",
    initials: "RN",
    description:
      "Full-service entertainment company founded by Jay-Z, covering management, publishing, touring, sports representation, and philanthropy.",
    relationship: "Active partner",
    dealsWithUs: 7,
    lastDeal: "Tidal Equity Deal - 2015",
    contacts: [
      {
        name: "Marcus Webb",
        title: "SVP Business Affairs",
        email: "marcus.webb@roc-nation.com",
        primary: true,
      },
      { name: "Desiree Perez", title: "COO", email: "desiree@roc-nation.com" },
    ],
    signals: [
      { tone: "positive", text: "Closed 3 prior deals with us - high trust." },
      { tone: "neutral", text: "Average response time: 14 hours." },
      { tone: "warning", text: "Pushed for tighter exclusivity in last 2 negotiations." },
    ],
    news: [
      { date: "Apr 22, 2026", title: "Roc Nation expands into K-pop publishing" },
      { date: "Mar 04, 2026", title: "Tidal restructures premium tier; revenue up 18%" },
    ],
  },
  "fentygroup.com": {
    domain: "fentygroup.com",
    company: "Fenty Corp",
    type: "Brand / Beauty",
    hq: "Los Angeles, CA",
    initials: "FC",
    description:
      "Rihanna's portfolio company spanning Fenty Beauty, Savage X Fenty, and Fenty Skin.",
    relationship: "Internal - Rihanna client",
    dealsWithUs: 4,
    lastDeal: "Savage x Fenty x Nike - In progress",
    contacts: [
      {
        name: "Dominique Azari",
        title: "VP Brand Partnerships",
        email: "d.azari@fentygroup.com",
        primary: true,
      },
    ],
    signals: [
      { tone: "positive", text: "Wholly aligned - Rihanna is principal." },
      { tone: "neutral", text: "Negotiations move via parallel outside counsel." },
    ],
    news: [{ date: "Apr 18, 2026", title: "Savage x Fenty opens new retail doors" }],
  },
  "klutchsports.com": {
    domain: "klutchsports.com",
    company: "Klutch Sports Group",
    type: "Sports Agency",
    hq: "Cleveland, OH",
    initials: "KS",
    description:
      "Sports agency founded by Rich Paul, representing major NBA talent and owned by United Talent Agency.",
    relationship: "Active partner",
    dealsWithUs: 5,
    lastDeal: "LeBron Nike Royalty Amendment - In progress",
    contacts: [
      {
        name: "Klutch Legal",
        title: "General Counsel",
        email: "legal@klutchsports.com",
        primary: true,
      },
      { name: "Rich Paul", title: "Founder and CEO", email: "rich.paul@klutchsports.com" },
    ],
    signals: [
      { tone: "positive", text: "Reliable counterparty - paper consistently clean." },
      { tone: "warning", text: "Aggressive on royalty floor; expect 3-4 redline rounds." },
    ],
    news: [{ date: "Apr 12, 2026", title: "Klutch expands sports representation team" }],
  },
};

export function getAriCounterpartyByEmail(email: string | undefined) {
  const domain = email?.split("@")[1];
  return domain ? ARI_COUNTERPARTY_PROFILES[domain] : undefined;
}

const ARI_THREAD_LINKED_CONTRACT_ID: Record<string, string> = {
  "ari-thread-1": DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
  "ari-thread-2": DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
  "ari-thread-3": DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
  "ari-thread-4": DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
  "ari-thread-5": DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
  "ari-thread-6": DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
  "ari-thread-7": DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
};

export function getAriThreadLinkedContract(threadId: string) {
  const contractId =
    ARI_THREAD_LINKED_CONTRACT_ID[threadId] ?? DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL;
  const contract = musicIndustryContracts.find((item) => item.contractId === contractId);
  if (!contract) return null;
  return { contractId: contract.contractId, name: contract.name };
}
